import type { ReactNode } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

/* ----------------------------------------------------------------------------
 * MobileDrawer — the collapsed engine rail + capability nav on phones/tablets
 * (< lg). A left slide-in panel with a scrim, focus trap, ESC / overlay close,
 * ARIA dialog semantics, and safe-area padding. Desktop (>= lg) never mounts
 * this: the persistent rails stay visible, so there is zero desktop impact.
 * -------------------------------------------------------------------------- */
export function MobileDrawer({
  open,
  onClose,
  title = 'Navigation',
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}) {
  useBodyScrollLock(open);
  const ref = useFocusTrap<HTMLDivElement>(open, onClose);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="absolute inset-0 bg-scrim/50 animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={ref}
        tabIndex={-1}
        className="absolute inset-y-0 left-0 flex w-[84vw] max-w-[320px] flex-col bg-surface shadow-lg outline-none animate-fade-in
                   pl-[env(safe-area-inset-left)] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
            {title}
          </span>
          <button
            onClick={onClose}
            aria-label="Close navigation"
            className="flex h-11 w-11 items-center justify-center rounded-md text-lg text-text-tertiary hover:bg-hover hover:text-text-primary"
          >
            <span aria-hidden>&#10005;</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>
      </div>
    </div>
  );
}
